import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { Survey, ReactSurveyElement, ReactElementFactory } from 'survey-react-ui';
import { Model, Serializer } from 'survey-core';
import { transformDataToSurveyJSON } from '../utils/surveyTransformer';

// CSS for version badges
const versionBadgeStyles = `
    .version-badge {
        display: inline-flex;
        align-items: center;
        padding: 1px 6px;
        margin-left: 6px;
        border-radius: 8px;
        font-size: 9px;
        font-weight: 500;
        letter-spacing: 0.2px;
        vertical-align: middle;
    }
    
    .version-badge-published {
        background: #dcfce7;
        color: #166534;
        border: 1px solid #bbf7d0;
    }
    
    .version-badge-draft {
        background: #fef3c7;
        color: #92400e;
        border: 1px solid #fde68a;
    }
    
    .question-title-with-version,
    .page-title-with-version {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 2px;
    }
    
    /* Ensure badges look good in SurveyJS table of contents */
    .sv-toc__item .version-badge {
        font-size: 8px;
        padding: 0px 4px;
        margin-left: 3px;
    }
    
    /* Preview mode header badge */
    .survey-preview-badge {
        background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
        color: white;
        padding: 4px 12px;
        border-radius: 16px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
    }
`;

// Register progress bar component (same as QuestionnaireView)
Serializer.addProperty("survey", "progressTitle");
class PercentageProgressBar extends ReactSurveyElement {
    render() {
        if (this.props.model.state === 'preview') {
            return null;
        }
        const progressValue = this.props.model.progressValue || 0;
        return (
            <div className="sv-progressbar-percentage-container">
                <div className="sv-progressbar-percentage__text">
                    <span className="sv-progressbar-percentage__title">{this.props.model.progressTitle}</span>
                    <span className="sv-progressbar-percentage__value">{progressValue + "%"}</span>
                </div>
                <div className="sv-progressbar-percentage__indicator">
                    {progressValue > 0 && (
                        <div
                            className="sv-progressbar-percentage__value-bar"
                            style={{ width: progressValue + "%" }}
                        ></div>
                    )}
                </div>
            </div>
        );
    }
}

// Only register if not already registered
if (!ReactElementFactory.Instance.isElementRegistered("sv-progressbar-percentage")) {
    ReactElementFactory.Instance.registerElement("sv-progressbar-percentage", props => {
        return React.createElement(PercentageProgressBar, props);
    });
}

const MemoizedSurvey = memo(Survey);

/**
 * QuestionnairePreview - Read-only preview of a questionnaire
 * Displays the questionnaire form based on the published snapshot
 * No save/submit functionality - display mode only
 * @param {string} questionnaireVersionId - The ID of the questionnaire version to preview
 * @param {boolean} includeDraft - Whether to include draft questions (default: false)
 */
function QuestionnairePreview({ jwt, questionnaireVersionId, includeDraft = true }) {
    const [surveyModel, setSurveyModel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [questionnaireVersion, setQuestionnaireVersion] = useState(null);

    useEffect(() => {
        const loadQuestionnairePreview = async () => {
            setIsLoading(true);
            setError(null);
            setSurveyModel(null);

            try {
                const api = axios.create({ 
                    headers: { Authorization: `Bearer ${jwt}` } 
                });

                // Call GetQuestionnairePreview endpoint
                const response = await api.post('/api/getQuestionnairePreview', {
                    questionnaireVersionId: questionnaireVersionId,
                    includeDraft: includeDraft
                });

                // Response is already parsed JSON from the Azure Function
                const schemaData = response.data.Questions;

                // Store version info for display
                if (response.data.Version) {
                    setQuestionnaireVersion(response.data.Version);
                }

                // Transform to SurveyJS format with version badges enabled for preview
                const surveyJson = transformDataToSurveyJSON(
                    { Questions: schemaData }, 
                    { includeVersionBadges: true }
                );

                const survey = new Model(surveyJson);
                survey.clearInvisibleValues = "onHidden";
                survey.showPreviewBeforeComplete = "noPreview";
                survey.showCompletedPage = false;
                survey.showCompleteButton = false;
                survey.progressTitle = "Questionnaire Preview";

                // Use edit mode with a value-reverting handler instead of SurveyJS's built-in mode: 'display'.
                // mode: 'display' fully disables interaction with controls, but for preview we want users
                // to be able to open dropdowns/multi-selects and inspect available options while still
                // keeping the survey effectively read-only.
                survey.onValueChanging.add((sender, options) => {
                    // Revert to original value, preventing any actual changes to responses in preview mode
                    options.value = options.oldValue;
                });

                // Disable validation for preview mode - allows free navigation without required field errors
                survey.validationEnabled = false;

                // Enable HTML rendering in question/page titles for version badges
                survey.onTextMarkdown.add((survey, options) => {
                    // Check if the text contains our version badge HTML
                    if (options.text?.includes('version-badge')) {
                        options.html = options.text;
                    }
                });

                // Add progress bar
                survey.addLayoutElement({
                    id: "progressbar-percentage",
                    component: "sv-progressbar-percentage",
                    container: "contentTop",
                    data: survey
                });

                setSurveyModel(survey);

            } catch (err) {
                console.error("Failed to load questionnaire snapshot:", err);
                setError(err.response?.data?.message || 'Failed to load questionnaire preview.');
            } finally {
                setIsLoading(false);
            }
        };

        if (questionnaireVersionId && jwt) {
            void loadQuestionnairePreview();
        } else if (!questionnaireVersionId) {
            setError('No questionnaire version ID provided.');
            setIsLoading(false);
        }
    }, [questionnaireVersionId, includeDraft, jwt]);

    if (isLoading) {
        return <div className="survey-loading-spinner"><div className="spinner"></div></div>;
    }

    if (error) {
        return (
            <div className="survey-error-alert">
                <h4>Error Loading Preview</h4>
                <p>{error}</p>
            </div>
        );
    }

    if (!surveyModel) {
        return <div className="survey-error-alert">Could not initialize the survey model.</div>;
    }

    return (
        <div className="survey-container">
            {/* Inject version badge styles */}
            <style>{versionBadgeStyles}</style>
            
            <header
                className="survey-header"
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1000
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span className="survey-preview-badge">Preview Mode</span>
                    {questionnaireVersion && (
                        <span className="questionnaire-version-badge">
                            v{questionnaireVersion}
                        </span>
                    )}
                </div>
            </header>

            <main className="survey-main-content">
                <MemoizedSurvey model={surveyModel} />
            </main>
        </div>
    );
}

export default QuestionnairePreview;
